import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box } from '@rsos/base-components';
import { ANI_ALI } from '@rsos/components/capstone/Location/constants/dataSources';
import { getDataSource } from '@rsos/components/capstone/Location/helpers';
import {
  hasNotificationPermission,
  pushNotification,
} from '@rsos/components/capstone/PushNotifications/PushNotifications';
import NoResults from '@rsos/components/capstone/Queue/NoResults';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { APPLICATION_AGENT_511 } from '@rsos/constants/locationApplications';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import { APPLE, SXM } from '@rsos/constants/orgsWithQueueText';
import { _911INFORM, _911_INFORM } from '@rsos/constants/spatialLayers';
import useFetchAgencyLinks from '@rsos/hooks/useFetchAgencyLinks';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  removeCall,
  setSelectedCallerID,
  inactiveCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  resetState,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import { useGetTransportsQuery } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import { getFordQueueDisplayName } from '@rsos/utils/getFordQueueDisplayName';
import getIsPrimaryChatTab from '@rsos/utils/getIsPrimaryChatTab';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import { isFordCallerID } from '@rsos/utils/isFordCallerID';
import { useBooleanFlagValue } from '@rsos/utils/openFeature';
import usePortalType from '@rsos/utils/usePortalType';
import { forceStaticLayerOn } from '../../../actions/spatialLayers';
import { getAirAmbulanceStatusByCallerID } from '../../AirAmbulanceForm/etl/etlStatusUtils';
import { transcriptionApi } from '../../JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import { isExtraInfoOrMeta } from '../../JurisdictionView/helpers';
import EmergencyCall from '../EmergencyCall/EmergencyCall';
import QueueNewMessagesIndicator from '../QueueNotifications/QueueNewMessagesIndicator';
import { QueueNotificationsProvider } from '../QueueNotifications/QueueNotificationsContext';
import { NoResultsWrapper, ScrollableList } from './EmergencyCallsQueue.styles';
import {
  trackClickCall,
  trackClickCallFromQueue,
  trackRemoveCall,
  trackTT911Request,
} from './emergencyCallsQueueTrackings';
import { renderLocationHelper } from './helpers';
import { applyCallFilters } from './utils';

export const NOTIFICATION_TT911_TITLE = 'New Text-to-911 from RapidSOS';

/**
 *
 * @param {object} emergencyCalls - list of 911 emergency calls, but if OutboundSMS is enabled it
 * can also contain TF911 conversation data.
 * [
 *  2223334444:{core:{locations}},
 *  5556667777:{textFrom911:{data}}
 * ]
 * @param {boolean} hasAgent511Access - does user have access to view Agent511 text-to-911 requests?
 * @param {String} hoveredCall - ID of call being hovered on the map or in the queue
 * @param {boolean} isVisible - is the current queue visible?
 * or caller ID for use with the Google Maps
 * @param {string} selectedCallerID - the selected caller id
 * @param {Function} onQueueHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {Function} onQueueItemClicked- callback when a queue item is clicked
 * @param {Function} onViewRcsEvent - function that the rcs toast action button uses to open relevant event
 */

const EmergencyCallsQueue = ({
  emergencyCalls = {},
  hasAgent511Access = false,
  hoveredCall = '',
  isVisible = false,
  selectedCallerID = '',
  irpVersion = null,
  onQueueHover = () => {},
  onRemoveID = () => {},
  onQueueItemClicked = () => {},
  onViewRcsEvent = () => {},
}) => {
  const dispatch = useDispatch();
  // Tracked as state (via callback ref) so the notifications observer is created
  // when the scroll container mounts, not only if it already exists on mount.
  const [scrollRoot, setScrollRoot] = useState(null);
  const psaps = useSelector(state => state.psaps);

  const sinatra = useSelector(state => state.sinatra);

  const hasAniAliAccess = checkAniAliAccessSelector({ psaps, sinatra });

  const hasCallStatusAccess = checkCallStatusAccessSelector({ psaps });

  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  const { requests: allAirAmbulances = { nonCalls: {}, calls: {} } } =
    useSelector(state => state.airAmbulance || {});

  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();
  const showRecentTranscripts = useBooleanFlagValue(
    'show_recent_transcripts',
    false,
  );
  const shouldFetchGmrResourceRequests = hasAirAmbulanceAccess;
  const { data: transportData } = useGetTransportsQuery(undefined, {
    skip: !shouldFetchGmrResourceRequests,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { callFilters, hasActiveCallFilters } = useCallQueueFilters();

  const application = sinatra.user.currentRole?.application;
  const workStation =
    sinatra.user.profile?.attributes?.[application]?.call_taker_mode_settings
      ?.workstation_position || null;

  /**
   * @constant {hoveredCall} - queue/map primary location
   * @constant {hoveredCallerID} - queue/map secondary location (deckGL)
   */
  const hoveredCallerID = useSelector(
    state => state?.hoveredIncident?.hoveredCallerID,
  );

  // If Agent511 is enabled, filter the emergency calls into 2 lists:
  // Text-to-911 requests and 911 calls. Text-to-911 requests will persist on top of the queue,
  // and 911 calls will display below text-to-911 requests.
  const tt911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.keys(emergencyCalls).filter(callerID => {
          const call = emergencyCalls[callerID];
          return (
            call?.isTT911 ||
            call.core?.locations[0]?.application === APPLICATION_AGENT_511
          );
        })
      : [];
  }, [emergencyCalls, hasAgent511Access]);

  const nonTT911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.keys(emergencyCalls).filter(callerID => {
          const call = emergencyCalls[callerID];
          return (
            !call?.isTT911 &&
            call.core?.locations[0]?.application !== APPLICATION_AGENT_511
          );
        })
      : [];
  }, [emergencyCalls, hasAgent511Access]);
  const [rightClickedCallerID, setRightClickedCallerID] = useState(null);
  const [isRemoveCallVisible, setIsRemoveCallVisible] = useState(false);
  const [callsClickedList, setCallsClickedList] = useState([]);
  const [newTT911RequestID, setNewTT911RequestID] = useState('');
  const { portalType } = usePortalType();
  const transcriptionQuerySubscriptionsRef = useRef(new Map());

  const isTranscriptionApiMiddlewareRegistered = useSelector(
    state => state?.transcriptionApi?.config?.middlewareRegistered === true,
  );

  const handleSelected = id => {
    const mostRecentLocation = getMostRecentLoc(emergencyCalls[id]);

    if (getIsPrimaryChatTab(mostRecentLocation)) {
      dispatch(setSelectedTab(CHAT_TAB));
    } else {
      dispatch(resetState());
    }

    if (!emergencyCalls[id]?.core?.locations) {
      return dispatch(setSelectedCallerID(id));
    }

    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
    const isConnectedCar =
      isCallerIDFromOrg(SXM, emergencyCalls, id) ||
      isFordCallerID(emergencyCalls, id);

    const isAniAli =
      mostRecentLocation?.application?.startsWith(ALI_UNDERSCORE);
    const serviceType = isAniAli ? ANI_ALI : getDataSource(mostRecentLocation);
    const classOfServices =
      mostRecentLocation?.class_of_service_category || null;

    const callStatus = getMostRecentCallStatus({
      callerID: id,
      callQueue: emergencyCalls,
      hasAniAliAccess,
      hasCallStatusAccess,
    });

    if (!callsClickedList.includes(id)) {
      trackClickCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        serviceType,
        classOfServices,
        callStatus,
      );
      setCallsClickedList(callsClickedList.concat(id));
    }

    trackClickCallFromQueue(
      isConnectedCar,
      irpVersion,
      payloadID,
      requestID,
      portalType,
      serviceType,
      classOfServices,
      callStatus,
    );
    dispatch(setSelectedCallerID(id));

    const has911informData =
      !!emergencyCalls?.[id]?.additionalData?.[_911_INFORM];

    if (has911informData) {
      dispatch(forceStaticLayerOn(_911INFORM));
    }
  };

  const handleRightClickCallerID = (e, callerID) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedCallerID !== callerID) setRightClickedCallerID(callerID);
  };

  const handleRemoveCall = id => {
    const hasCoreLocations = emergencyCalls[id.caller_id]?.core?.locations;
    if (hasCoreLocations) {
      const mostRecentLocation = getMostRecentLoc(emergencyCalls[id.caller_id]);
      const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);

      const isConnectedCar =
        isCallerIDFromOrg(SXM, emergencyCalls, id.caller_id) ||
        isFordCallerID(emergencyCalls, id.caller_id);

      const callStatus = getMostRecentCallStatus({
        callerID: id.caller_id,
        callQueue: emergencyCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      trackRemoveCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        callStatus,
      );
    }
    dispatch(inactiveCalls(id));
    onRemoveID(id.caller_id);
    dispatch(removeCall(id.caller_id));
    setIsRemoveCallVisible(false);
    dispatch(setHoveredID(null));
    toast.dismiss(`toastRcsAvailable-${id.caller_id}`);
  };

  const onClickHandler = useCallback(() => {
    setRightClickedCallerID(null);
  }, []);

  window.oncontextmenu = () =>
    window.removeEventListener('click', onClickHandler, false);

  useEffect(() => {
    window.addEventListener('click', onClickHandler, false);

    return () => {
      window.removeEventListener('click', onClickHandler, false);
    };
  }, [onClickHandler]);

  const shouldDisplayCall = useCallback(
    item => {
      if (!hasActiveCallFilters) return true;
      return applyCallFilters(item, callFilters, workStation);
    },
    [callFilters, hasActiveCallFilters, workStation],
  );

  const hasAdminCallIndicatorEnabled = useBooleanFlagValue(
    'admin_call_indicator',
    false,
  );
  const airAmbulanceStatusByCallerID = useMemo(
    () => getAirAmbulanceStatusByCallerID(transportData?.transports),
    [transportData?.transports],
  );

  useEffect(() => {
    if (!showRecentTranscripts || !isTranscriptionApiMiddlewareRegistered) {
      transcriptionQuerySubscriptionsRef.current.forEach(subscription => {
        subscription?.unsubscribe?.();
      });
      transcriptionQuerySubscriptionsRef.current.clear();
      return;
    }

    const queueCallerIds = Object.keys(emergencyCalls || {});
    const queueCallerSet = new Set(queueCallerIds);

    queueCallerIds.forEach(callerId => {
      if (transcriptionQuerySubscriptionsRef.current.has(callerId)) return;

      const location0 = emergencyCalls?.[callerId]?.core?.locations?.[0];
      const conversationStart = location0?.conversation_start_time;
      const callStart = location0?.call_start_time;
      const callStartTime =
        typeof conversationStart === 'number'
          ? conversationStart
          : typeof callStart === 'number'
            ? callStart
            : null;

      const subscription = dispatch(
        transcriptionApi.endpoints.fetchTranscriptions.initiate({
          callerId,
          callStartTime,
        }),
      );
      transcriptionQuerySubscriptionsRef.current.set(callerId, subscription);
    });

    transcriptionQuerySubscriptionsRef.current.forEach(
      (subscription, callerId) => {
        if (queueCallerSet.has(callerId)) return;
        subscription?.unsubscribe?.();
        transcriptionQuerySubscriptionsRef.current.delete(callerId);
      },
    );
  }, [
    dispatch,
    emergencyCalls,
    isTranscriptionApiMiddlewareRegistered,
    showRecentTranscripts,
  ]);

  useEffect(() => {
    const subscriptions = transcriptionQuerySubscriptionsRef.current;
    return () => {
      subscriptions.forEach(subscription => {
        subscription?.unsubscribe?.();
      });
      subscriptions.clear();
    };
  }, []);

  /**
   * @function renderQueueItem - reorder the callQueue based on the latest location or secondaryLocation
   */
  const renderQueueItem = queue =>
    queue.map(callerID => {
      const item = emergencyCalls[callerID];
      const {
        mostRecentLocRecord,
        isManualSearch,
        isCallerIn911informBuilding,
        isCallerInConnectedSitesBuilding,
        hasGoogleCarCrashData,
        hasGoogleCallerInfo,
        isTT911,
        isAdmin,
        source,
        conversation_id,
      } = renderLocationHelper(item, emergencyCalls, hasAgent511Access);

      const callStatus = getMostRecentCallStatus({
        callerID,
        callQueue: emergencyCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });
      const isFord = isFordCallerID(emergencyCalls, callerID);
      const isSxmConnectedCar = isCallerIDFromOrg(
        SXM,
        emergencyCalls,
        callerID,
      );

      //TODO vkoo: investigate why this condition is necessary. PR #21382
      if (!callerID) {
        return null;
      }

      return (
        <Box
          callerid={callerID}
          key={`${callerID}`}
          noShrink={true}
          onContextMenu={e => {
            handleRightClickCallerID(e, callerID);
          }}
          onMouseEnter={() => onQueueHover(callerID, 'queue', EMERGENCY_CALLS)}
          onMouseLeave={() => onQueueHover(null, 'queue', EMERGENCY_CALLS)}
          onClick={() => onQueueItemClicked(callerID)}
          data-name={`${source}-${callerID}`}
          display={shouldDisplayCall(item) ? 'flex' : 'none'}
        >
          <EmergencyCall
            applicationType={mostRecentLocRecord.application}
            classOfService={mostRecentLocRecord?.class_of_service_category}
            callerID={callerID}
            callStatus={callStatus}
            airAmbulanceStatus={airAmbulanceStatusByCallerID[callerID]}
            rightClickedCallerID={rightClickedCallerID}
            hasAgent511Access={hasAgent511Access}
            hasGoogleCarCrashData={hasGoogleCarCrashData}
            hasGoogleCallerInfo={hasGoogleCallerInfo}
            isApple={isCallerIDFromOrg(APPLE, emergencyCalls, callerID)}
            isCallerIn911informBuilding={isCallerIn911informBuilding}
            isCallerInConnectedSitesBuilding={isCallerInConnectedSitesBuilding}
            isCallReceived={item?.agencyShareChat?.isCallReceived}
            isCallShared={item?.agencyShareChat?.isCallShared}
            isConnectedCar={isSxmConnectedCar || isFord}
            isFord={isFord}
            primaryLabel={
              isFord ? getFordQueueDisplayName(item?.additionalData?.ford) : ''
            }
            isManualSearch={isManualSearch}
            isMapMarkerHovered={
              callerID === hoveredCall || callerID === hoveredCallerID
            }
            isRemoveCallVisible={isRemoveCallVisible}
            isSelected={callerID === selectedCallerID}
            isTT911={isTT911}
            isAdmin={isAdmin && hasAdminCallIndicatorEnabled}
            isUnread={
              item?.showNewCallNotification || item?.agencyShareChat?.isUnread
            }
            ui={item?.ui}
            isUnseen={item?.agencyShareChat?.isUnseen}
            onRemoveCall={handleRemoveCall}
            setCallerID={handleSelected}
            source={source}
            tt911ADR={item?.additionalData?.[APPLICATION_AGENT_511]?.[0]}
            conversationID={conversation_id}
            onViewRcsEvent={onViewRcsEvent}
          />
        </Box>
      );
    });

  useEffect(() => {
    if (
      tt911Requests &&
      tt911Requests.length &&
      numberWithNewLocation &&
      hasNotificationPermission()
    ) {
      const tt911Request = tt911Requests.find(
        tt911ID =>
          emergencyCalls[tt911ID]?.core?.locations?.length === 1 &&
          emergencyCalls[tt911ID]?.core?.locations?.[0].caller_id ===
            numberWithNewLocation,
      );
      if (tt911Request) {
        setNewTT911RequestID(tt911Request);
      }
    }
  }, [tt911Requests, numberWithNewLocation, irpVersion, emergencyCalls]);

  useEffect(() => {
    if (newTT911RequestID) {
      pushNotification({
        title: NOTIFICATION_TT911_TITLE,
      });
      setNewTT911RequestID('');
    }
  }, [newTT911RequestID]);

  useEffect(() => {
    if (newTT911RequestID && emergencyCalls[newTT911RequestID]) {
      const callLocations = emergencyCalls[newTT911RequestID].core.locations;
      const firstLocationIndex = callLocations?.length - 1 || 0;
      const { payloadID, requestID } = isExtraInfoOrMeta(
        callLocations[firstLocationIndex],
      );
      trackTT911Request(irpVersion, payloadID, requestID);
    }
  }, [emergencyCalls, irpVersion, newTT911RequestID]);

  // Check agency share/chat access and fetch paired peer agencies.
  useFetchAgencyLinks();

  return (
    isVisible && (
      <QueueNotificationsProvider scrollRoot={scrollRoot}>
        {Object.keys(emergencyCalls).length ||
        Object.keys(allAirAmbulances?.nonCalls || {}).length ? (
          <Box position="relative" flex="1 1 auto" minHeight="0">
            <ScrollableList ref={setScrollRoot}>
              {hasAgent511Access ? (
                <>
                  <Box data-name="tt911-requests">
                    {renderQueueItem(tt911Requests)}
                  </Box>
                  <Box data-name="non-tt911-requests">
                    {renderQueueItem(nonTT911Requests)}
                  </Box>
                </>
              ) : (
                <Box data-name="emergency-calls">
                  {renderQueueItem(Object.keys(emergencyCalls))}
                </Box>
              )}
            </ScrollableList>
            <QueueNewMessagesIndicator />
          </Box>
        ) : (
          <NoResultsWrapper>
            <NoResults
              title="No Emergencies"
              description="No emergency events were found."
            />
          </NoResultsWrapper>
        )}
      </QueueNotificationsProvider>
    )
  );
};

export default EmergencyCallsQueue;
